/**
 * SibaScore Cache admin card — top queries by hit count + purge control.
 * All searches (both cache hits and misses) are persisted permanently to
 * the `sibascore_query_stats` collection; the LLM cache itself expires in 24h.
 */
import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Database, RefreshCcw, Trash2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

interface CacheRow {
  normalized_query: string;
  original_query: string;
  total_searches: number;
  last_score: number | null;
  last_tier: string | null;
  first_seen: string | null;
  last_seen: string | null;
}
interface CacheResponse {
  rows: CacheRow[];
  summary: {
    unique_queries: number;
    total_searches: number;
    live_cache_rows: number;
  };
}

const fmt = (iso?: string | null) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export const SibaScoreCacheCard: React.FC = () => {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const readonly = (me?.role || "user") !== "admin";

  const q = useQuery<CacheResponse>({
    queryKey: ["admin-sibascore-cache"],
    queryFn: async () =>
      (await api.get("/admin/sibascore/cache?limit=20")).data,
    refetchOnWindowFocus: false,
  });

  const purge = useMutation({
    mutationFn: async () =>
      (await api.post("/admin/sibascore/cache/purge")).data,
    onSuccess: (d: { cache_deleted: number; stats_deleted: number }) => {
      toast.success(
        `Purged — ${d.cache_deleted} cache rows, ${d.stats_deleted} query stats.`,
      );
      qc.invalidateQueries({ queryKey: ["admin-sibascore-cache"] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail || "Purge failed"),
  });

  const rows = q.data?.rows || [];
  const summary = q.data?.summary;

  return (
    <div className="sg-card p-6" data-testid="sibascore-cache-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 flex items-center justify-center shrink-0">
          <Database className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-lg font-bold text-[color:var(--sg-fg)]">
            SibaScore Search Cache
          </div>
          <div className="text-xs text-[color:var(--sg-muted)] mt-0.5 leading-snug">
            Top all-time queries, hit counts, and a purge control. Every search
            is stored permanently; the LLM response layer expires per row after
            24 h.
          </div>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          data-testid="sibascore-cache-refresh"
          onClick={() => q.refetch()}
          disabled={q.isFetching}
          className="h-8 text-xs"
        >
          {q.isFetching
            ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            : <RefreshCcw className="h-3 w-3 mr-1" />}
          Refresh
        </Button>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-3 gap-3 mb-4" data-testid="sibascore-cache-summary">
        <SummaryTile label="Unique queries" value={summary?.unique_queries ?? 0} testId="cache-unique" />
        <SummaryTile label="Total searches" value={summary?.total_searches ?? 0} testId="cache-total" />
        <SummaryTile label="Live cached (24h)" value={summary?.live_cache_rows ?? 0} testId="cache-live" />
      </div>

      {/* Rows */}
      <div className="rounded-lg border border-[color:var(--sg-border)] overflow-hidden">
        <div className="hidden md:grid grid-cols-[1fr_90px_110px_180px] gap-2 px-3 py-2 text-[10px] uppercase tracking-wider text-[color:var(--sg-muted)] font-semibold bg-[color:var(--sg-muted-bg)]/60">
          <div>Query</div>
          <div className="text-right">Searches</div>
          <div>Last tier · score</div>
          <div>Last seen</div>
        </div>
        {q.isLoading && (
          <div className="p-6 text-center text-xs text-[color:var(--sg-muted)]">
            Loading…
          </div>
        )}
        {!q.isLoading && rows.length === 0 && (
          <div className="p-6 text-center text-xs text-[color:var(--sg-muted)]" data-testid="sibascore-cache-empty">
            No searches have been made yet.
          </div>
        )}
        {rows.map((r, i) => (
          <div
            key={r.normalized_query}
            data-testid={`sibascore-cache-row-${i}`}
            className="grid grid-cols-[1fr_90px_110px_180px] gap-2 items-center px-3 py-2.5 border-t border-[color:var(--sg-border)] text-[13px]"
          >
            <div className="min-w-0 truncate font-medium text-[color:var(--sg-fg)]" title={r.original_query}>
              {r.original_query}
              <span className="ml-2 text-[10px] text-[color:var(--sg-muted)] font-mono">
                {r.normalized_query}
              </span>
            </div>
            <div className="text-right font-bricolage font-bold text-[color:var(--sg-fg)] tabular-nums">
              {r.total_searches}
            </div>
            <div className="text-[color:var(--sg-muted)]">
              {r.last_tier ? (
                <span>
                  <span className="text-[color:var(--sg-fg)] font-semibold">{r.last_tier}</span>
                  {typeof r.last_score === "number" && (
                    <span className="text-[11px] ml-1">· {r.last_score}</span>
                  )}
                </span>
              ) : "—"}
            </div>
            <div className="text-[11px] text-[color:var(--sg-muted)]">
              {fmt(r.last_seen)}
            </div>
          </div>
        ))}
      </div>

      {/* Purge */}
      {!readonly && (
        <div className="mt-4 flex items-center justify-end">
          <Button
            type="button"
            size="sm"
            variant="outline"
            data-testid="sibascore-cache-purge"
            disabled={purge.isPending || q.isLoading}
            onClick={() => {
              if (
                window.confirm(
                  "Purge ALL SibaScore cache + search stats? This clears the live LLM cache AND the permanent search-count history.",
                )
              ) {
                purge.mutate();
              }
            }}
            className="h-9 border-red-200 text-red-700 hover:bg-red-50 dark:border-red-900 dark:text-red-300 dark:hover:bg-red-950/40"
          >
            {purge.isPending
              ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              : <Trash2 className="h-3.5 w-3.5 mr-1" />}
            Purge cache & stats
          </Button>
        </div>
      )}
    </div>
  );
};

const SummaryTile: React.FC<{ label: string; value: number; testId?: string }> = ({
  label, value, testId,
}) => (
  <div
    data-testid={testId}
    className="rounded-lg border border-[color:var(--sg-border)] bg-[color:var(--sg-muted-bg)]/40 px-3 py-2.5"
  >
    <div className="font-bricolage font-bold text-xl text-[color:var(--sg-fg)] tabular-nums">
      {value.toLocaleString()}
    </div>
    <div className="text-[10px] uppercase tracking-wider text-[color:var(--sg-muted)] font-semibold mt-0.5">
      {label}
    </div>
  </div>
);

export default SibaScoreCacheCard;
